/**
 * G2eGente - Página de Acesso do Funcionário via Link da Empresa
 * Rota: /app/e/:code
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { 
  Loader2, 
  Building2, 
  User, 
  Lock, 
  Eye, 
  EyeOff,
  AlertTriangle,
  CheckCircle,
  Smartphone,
  Mail,
  Phone
} from 'lucide-react';
import { companiesAPI } from '../../lib/api';
import { toast } from 'sonner';

export default function EmployeeAccess() {
  const { code } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Login state
  const [cpf, setCpf] = useState('');
  const [password, setPassword] = useState('');
  
  // Contact update state (NOVA ETAPA)
  const [showContactUpdate, setShowContactUpdate] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [loginResult, setLoginResult] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    fetchCompanyInfo();
  }, [code]);

  const fetchCompanyInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await companiesAPI.getByAccessCode(code);
      setCompanyInfo(res.data);
    } catch (err) {
      setError('Código de acesso inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return `(${numbers}`;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handleCPFChange = (e) => {
    const formatted = formatCPF(e.target.value);
    if (formatted.replace(/\D/g, '').length <= 11) {
      setCpf(formatted);
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    if (formatted.replace(/\D/g, '').length <= 11) {
      setContactPhone(formatted);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!cpf || !password) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    try {
      setSubmitting(true);
      const res = await companiesAPI.employeeLogin({
        company_id: companyInfo.company_id,
        cpf: cpf,
        password: password
      });
      
      setLoginResult(res.data);
      
      // Preencher dados existentes se houver
      if (res.data.employee.email) {
        setContactEmail(res.data.employee.email);
      }
      if (res.data.employee.telefone) {
        setContactPhone(res.data.employee.telefone);
      }
      
      // Verificar se precisa completar dados de contato
      if (res.data.needs_contact_update) {
        setShowContactUpdate(true);
      } else if (res.data.can_change_password && res.data.is_first_access) {
        // Se não precisa de contato mas pode alterar senha
        setShowChangePassword(true);
      } else {
        // Salvar token padrão (compatível com AuthContext)
        localStorage.setItem('token', res.data.access_token);
        
        toast.success(`Bem-vindo, ${res.data.employee.nome}!`);
        // Recarregar a página para que o AuthContext detecte o novo token
        window.location.href = '/m/employee';
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveContact = async () => {
    // Validar email
    if (contactEmail) {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailPattern.test(contactEmail)) {
        toast.error('Email inválido');
        return;
      }
    }
    
    // Validar telefone
    if (contactPhone) {
      const phoneClean = contactPhone.replace(/\D/g, '');
      if (phoneClean.length < 10 || phoneClean.length > 11) {
        toast.error('Telefone deve ter 10 ou 11 dígitos');
        return;
      }
    }
    
    try {
      setSubmitting(true);
      await companiesAPI.employeeUpdateContact({
        employee_id: loginResult.employee.id,
        email: contactEmail,
        telefone: contactPhone
      });
      
      toast.success('Dados de contato salvos!');
      
      // Verificar se deve ir para alteração de senha ou finalizar
      if (loginResult.can_change_password && loginResult.is_first_access) {
        setShowContactUpdate(false);
        setShowChangePassword(true);
      } else {
        // Salvar token e redirecionar
        localStorage.setItem('token', loginResult.access_token);
        window.location.href = '/m/employee';
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar dados');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkipContact = () => {
    // Verificar se deve ir para alteração de senha ou finalizar
    if (loginResult.can_change_password && loginResult.is_first_access) {
      setShowContactUpdate(false);
      setShowChangePassword(true);
    } else {
      // Salvar token e redirecionar
      localStorage.setItem('token', loginResult.access_token);
      toast.success(`Bem-vindo, ${loginResult.employee.nome}!`);
      window.location.href = '/m/employee';
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Preencha a nova senha');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    if (newPassword.length < 4) {
      toast.error('A senha deve ter pelo menos 4 caracteres');
      return;
    }
    
    try {
      setSubmitting(true);
      await companiesAPI.employeeChangePassword({
        employee_id: loginResult.employee.id,
        current_password: password,
        new_password: newPassword
      });
      
      // Salvar token padrão (compatível com AuthContext)
      localStorage.setItem('token', loginResult.access_token);
      
      toast.success('Senha alterada com sucesso!');
      // Recarregar a página para que o AuthContext detecte o novo token
      window.location.href = '/m/employee';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkipPasswordChange = () => {
    // Salvar token padrão e redirecionar
    localStorage.setItem('token', loginResult.access_token);
    
    toast.success(`Bem-vindo, ${loginResult.employee.nome}!`);
    // Recarregar a página para que o AuthContext detecte o novo token
    window.location.href = '/m/employee';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-cyan-900 to-slate-900 flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-cyan-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Link Inválido</h2>
            <p className="text-slate-600 mb-4">{error}</p>
            <p className="text-sm text-slate-500">
              Solicite um novo link de acesso ao RH da sua empresa.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tela de completar dados de contato (NOVA)
  if (showContactUpdate && loginResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-cyan-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-cyan-600" />
            </div>
            <CardTitle>Complete seu Cadastro</CardTitle>
            <CardDescription>
              Informe seus dados de contato para receber notificações importantes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-cyan-50 border-cyan-200">
              <CheckCircle className="w-4 h-4 text-cyan-600" />
              <AlertDescription className="text-cyan-700">
                Olá, <strong>{loginResult.employee.nome}</strong>!
              </AlertDescription>
            </Alert>

            <div>
              <Label htmlFor="contactEmail">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="contactEmail"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="seu.email@exemplo.com"
                  className="pl-10"
                  data-testid="employee-contact-email"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Para receber comunicados da empresa
              </p>
            </div>

            <div>
              <Label htmlFor="contactPhone">Celular</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="contactPhone"
                  type="tel"
                  value={contactPhone}
                  onChange={handlePhoneChange}
                  placeholder="(00) 00000-0000"
                  className="pl-10"
                  data-testid="employee-contact-phone"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Para contato direto quando necessário
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={handleSkipContact}
                className="flex-1"
                disabled={submitting}
                data-testid="employee-skip-contact-btn"
              >
                Pular por agora
              </Button>
              <Button
                onClick={handleSaveContact}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                disabled={submitting}
                data-testid="employee-save-contact-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continuar'}
              </Button>
            </div>

            <p className="text-center text-xs text-slate-500">
              Você pode completar depois nas configurações do app.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Modal de alteração de senha
  if (showChangePassword && loginResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-cyan-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-cyan-600" />
            </div>
            <CardTitle>Criar Nova Senha</CardTitle>
            <CardDescription>
              Por segurança, crie uma senha personalizada para acessar o app.
              <br />
              <span className="text-amber-600 font-medium">Você só pode alterar a senha uma vez.</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-cyan-50 border-cyan-200">
              <CheckCircle className="w-4 h-4 text-cyan-600" />
              <AlertDescription className="text-cyan-700">
                Olá, {loginResult.employee.nome}!
              </AlertDescription>
            </Alert>

            <div>
              <Label htmlFor="newPassword">Nova Senha</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Digite sua nova senha"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirmar Senha</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirme sua nova senha"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleSkipPasswordChange}
                className="flex-1"
                disabled={submitting}
                data-testid="employee-skip-password-btn"
              >
                Manter Senha Atual
              </Button>
              <Button
                onClick={handleChangePassword}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                disabled={submitting}
                data-testid="employee-save-password-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-cyan-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {/* Logo/Icon */}
          <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Smartphone className="w-10 h-10 text-white" />
          </div>
          
          <CardTitle className="text-2xl">G2eGente</CardTitle>
          <CardDescription>Acesso do Funcionário</CardDescription>
        </CardHeader>
        
        <CardContent>
          {/* Company Info */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              {companyInfo.logo_url ? (
                <img 
                  src={companyInfo.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${companyInfo.logo_url}` : companyInfo.logo_url} 
                  alt={companyInfo.company_name}
                  className="w-12 h-12 rounded-lg object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`w-12 h-12 bg-cyan-100 rounded-lg items-center justify-center ${companyInfo.logo_url ? 'hidden' : 'flex'}`}>
                <Building2 className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{companyInfo.company_name}</p>
                <p className="text-sm text-slate-500">CNPJ: {companyInfo.cnpj}</p>
              </div>
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="cpf">CPF</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="cpf"
                  data-testid="employee-cpf-input"
                  type="text"
                  value={cpf}
                  onChange={handleCPFChange}
                  placeholder="000.000.000-00"
                  className="pl-10"
                  autoComplete="off"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  data-testid="employee-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite sua senha"
                  className="pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Primeiro acesso? Use os 4 primeiros dígitos do seu CPF.
              </p>
            </div>

            <Button 
              type="submit" 
              data-testid="employee-login-submit"
              className="w-full bg-cyan-600 hover:bg-cyan-700"
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Entrar
            </Button>
          </form>

          <p className="text-center text-xs text-slate-500 mt-6">
            Problemas para acessar? Fale com o RH da sua empresa.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
